import { Button, message, Spin } from 'antd';
import Card from 'components/Card/Card';
import { useState } from 'react';
import { useAsync } from 'react-async-hook';
import { familyService } from 'services';
import { FileList, GetFileContentsEntityTypes } from 'components/FileList/FileList';
import TabularList from 'components/TabularList/TabularList';
import { FAMILY_FILE_TYPES } from 'services/family.service';
import { Download } from '@carbon/icons-react';

export function FamilyDocuments({ application, household }) {
  const [allFilesDownloading, setAllFilesDownloading] = useState(false);

  // Fetch application files
  const [applicationFiles, setApplicationFiles] = useState(null);
  useAsync(async () => {
    if (application?.id !== undefined) {
      try {
        const _applicationFiles = await familyService.getApplicationFiles(application?.id);
        setApplicationFiles(_applicationFiles);
      } catch (error) {
        console.error('Error fetching application files', error);
        message.error('Something went wrong');
      }
    }
  }, [application]);

  if (applicationFiles === null) {
    return <Spin />;
  }

  // Filter files by type - based on the screenshot
  const _applicationFiles = applicationFiles?.filter((file) => file?.fileType === FAMILY_FILE_TYPES.APPLICATION?.code);
  const _sponsorLesFiles = applicationFiles?.filter((file) => file?.fileType === FAMILY_FILE_TYPES.SPONSOR_LES.code);
  const _ordersFiles = applicationFiles?.filter((file) => file?.fileType === FAMILY_FILE_TYPES.ORDERS.code);
  const _sponsorOtherFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.SPONSOR_OTHER.code,
  );
  const _spouseWorkFiles = applicationFiles?.filter((file) => file?.fileType === FAMILY_FILE_TYPES.SPOUSE_WORK.code);
  const _spousePaystubFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.SPOUSE_PAYSTUB.code,
  );
  const _spouseSchoolFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.SPOUSE_SCHOOL.code,
  );
  const _childCustodyFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.CHILD_CUSTODY.code,
  );
  const _exceptionRequestFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.EXCEPTION_REQUEST?.code,
  );
  const _selfCertificationFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.SELF_CERTIFICATION.code,
  );
  const _providerRatesFiles = applicationFiles?.filter(
    (file) => file?.fileType === FAMILY_FILE_TYPES.PROVIDER_RATES.code,
  );
  const _childCysFiles = applicationFiles?.filter((file) => file?.fileType === FAMILY_FILE_TYPES.CHILD_CYS.code);

  const _fileListProps = {
    entityType: GetFileContentsEntityTypes.FAMILY,
    applicationId: application?.id,
  };

  return (
    <Card
      title="Documents"
      extra={
        <Button
          onClick={async () => {
            setAllFilesDownloading(true);

            try {
              const response = await familyService.downloadAllApplicationFiles(application?.id);

              // Create a temporary link element to trigger the download
              const a = document.createElement('a');
              a.href = response.downloadUrl;
              a.download = `Family_Application_${application?.id}_documents.zip`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            } catch (error) {
              console.error('Error downloading all files', error);
              message.error('Something went wrong');
            } finally {
              setAllFilesDownloading(false);
            }
          }}
          icon={<Download />}
          loading={allFilesDownloading}
          size="small"
          type="primary"
        >
          Download all documents
        </Button>
      }
    >
      <TabularList
        className="mt-8"
        data={[
          {
            title: 'Application',
            value: <FileList {..._fileListProps} files={_applicationFiles} />,
          },
          {
            title: 'Military Pay & Benefits',
            value: <FileList {..._fileListProps} files={_sponsorLesFiles} />,
          },
          {
            title: 'Military Work Verification',
            value: <FileList {..._fileListProps} files={_ordersFiles} />,
          },
          {
            title: 'Sole Parent Custody/Legal Papers',
            value: <FileList {..._fileListProps} files={_sponsorOtherFiles} />,
          },
          {
            title: 'Work Verification',
            value: <FileList {..._fileListProps} files={_spouseWorkFiles} />,
          },
          {
            title: 'Pay Documents',
            value: <FileList {..._fileListProps} files={_spousePaystubFiles} />,
          },
          {
            title: 'School Enrollment',
            value: <FileList {..._fileListProps} files={_spouseSchoolFiles} />,
          },
          {
            title: 'Custody/Legal Papers',
            value: <FileList {..._fileListProps} files={_childCustodyFiles} />,
          },
          {
            title: 'Special Circumstance Documents',
            value: <FileList {..._fileListProps} files={_exceptionRequestFiles} />,
          },
          {
            title: "Proof of Child's Dependency",
            value: <FileList {..._fileListProps} files={_selfCertificationFiles} />,
          },
          {
            title: 'Child Care Provider Form',
            titleRequired: true,
            value: <FileList {..._fileListProps} files={_providerRatesFiles} />,
          },
          {
            title: 'On-Base Care Exempt Waiver',
            titleRequired: true,
            value: <FileList {..._fileListProps} files={_childCysFiles} />,
          },
        ]}
      />
    </Card>
  );
}
